import React, { useMemo, useState } from 'react';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import { translate } from '../../../services/translate';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import useFilters from '../../../components/Filter/hooks/useFilters';
import { CitizenParamList } from '../../../typings/Params';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import FilterInput from '../../../components/Filter/components/FilterInput';
import { NativeSyntheticEvent, TextInputKeyPressEventData, View } from 'react-native';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../context/Auth';
import { citizenService } from '../../../services/central-api/citizen';
import MyInfinityDataTable from '../../../components/MyInfinityDataTable';
import PlateImage from '../../../components/lpr/PlateImage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MyAppText from '../../../components/MyAppText';
import moment from 'moment';
import { IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import Filter from './Filters';
import { faCalendar } from '@fortawesome/pro-regular-svg-icons';

const limit = 100;

export default function CitizenLprSearch({ navigation }: { navigation: Navigation; }) {
    const { styles } = useStyles(stylesheet);
    const { user: authenticatedUser } = useAuth();
    const route = useRoute<RouteProp<CitizenParamList, 'CitizenLprSearch'>>();

    const [filters, setFilters] = useFilters<CitizenParamList['CitizenLprSearch']>({
        ...route.params,
    });
    const [plate, setPlate] = useState(filters.plate ?? '');
    const isFocused = useIsFocused();
    const { data, hasNextPage, fetchNextPage, isLoading, isFetchingNextPage } = useInfiniteQuery({
        queryKey: ['CitizenLprSearch', filters],
        queryFn: ({ pageParam = 0 }) => {
            return citizenService.getCitizensLprDetections({
                ...filters,
                limit,
                page: pageParam,
            });
        },
        getNextPageParam: (lastPage, allPages) =>
            lastPage.length < limit ? undefined : allPages.length,
        enabled: !!authenticatedUser && isFocused,
    });

    const occurrences = useMemo(
        () => data?.pages.flat() ?? [],
        [data]
    );

    const { data: countData, isFetching: countIsLoading } = useQuery({
        queryKey: ['getCitizensLprCountDetections', filters],
        queryFn: () => citizenService.getCitizensLprCountDetections(filters),
        enabled: isFocused,
    });

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            navigation.setParams({
                ...filters,
                plate: plate !== '' ? plate.toUpperCase() : undefined,
            });
            setFilters(old => ({
                ...old,
                plate: plate !== '' ? plate.toUpperCase() : undefined,
            }));
        }
    }

    return (<Container>
        <Header title={translate('plateSearch')} />
        <Card>
            <DataTableHeader
                title={`${!countIsLoading && countData ? countData.count + ' ' : ''} ${translate('registeredDetections')}`}
                filter={<Filter
                    filters={filters}
                    setFilters={setFilters}
                    plateFilter={plate}
                    setPlateFilter={setPlate}
                />}
                zIndex={2}
                filterInput={<FilterInput
                    value={plate}
                    placeholder={translate('typeToSearch')}
                    onChange={setPlate}
                    onKeyPress={handleKeyDown}
                    maxCharacters={7}
                    toUppercase={true}
                />}
            />
            <DataTableContainer
                dataTable={<MyInfinityDataTable
                    progressPending={isLoading}
                    data={occurrences}
                    onRowPress={(row) => {
                        navigation.navigate('CitizenLprEventsDetails', { id: row.id });
                    }}
                    columns={[{
                        name: translate('capturedImage'),
                        cell: row => <View style={{ paddingVertical: 14 }}>
                            <PlateImage
                                src={row.imageUrl}
                                platePosition={row.platePosition}
                            />
                        </View>,
                        flex: 1,
                    }, {
                        name: translate('plate'),
                        selector: row => row.plate,
                        flex: 1,
                    }, {
                        name: translate('description'),
                        selector: row => row.description,
                        flex: 1,
                    }, {
                        name: translate('readingCapturedBy'),
                        selector: row => row.citizen.name,
                        flex: 1,
                    }, {
                        name: translate('location'),
                        flex: 1,
                        cell: row => <View style={{ flexBasis: '20%', rowGap: 10, paddingVertical: 8 }}>
                            <View style={styles.locationView} >
                                <FontAwesomeIcon icon={faCalendar as IconDefinition} fontSize={13} style={styles.locationIcon} />
                                <MyAppText style={styles.locationText}>
                                    {moment(new Date(row.time)).format('DD/MM/YYYY HH:mm:ss')}
                                </MyAppText>
                            </View>
                        </View>
                    }]}
                    loadMore={() => {
                        if (!hasNextPage) {
                            return;
                        }
                        if (isFetchingNextPage) {
                            return;
                        }
                        fetchNextPage();
                    }}
                />}
            />
        </Card>
    </Container>);
}

const stylesheet = createStyleSheet(theme => ({
    headerCell: {
        fontSize: 14,
        color: theme.colors.labelColor,
    },
    locationView: {
        flexDirection: 'row',
        alignItems: 'center',
        columnGap: 5
    },
    locationIcon: {
        color: theme.colors.textColor,
    },
    locationText: {
        color: theme.colors.disabledText,
        fontWeight: '500',
    },
}));
